import { MapPin, Star, Save, FileSearch, Mail, ExternalLink, Globe, Clock, Tag, Activity, Linkedin, Instagram, UserCircle2, Layers, Send } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { EmailAction, PhoneAction } from "./ContactActions";

function badgeMeta(badge) {
  if (badge === "QUENTE") return { cls: "al-badge al-badge-quente", dot: "#EF4444", label: "QUENTE" };
  if (badge === "FRIO") return { cls: "al-badge al-badge-frio", dot: "#22C55E", label: "FRIO" };
  return { cls: "al-badge al-badge-morno", dot: "#F97316", label: "MORNO" };
}

function statusMeta(status, t) {
  const s = (status || "").toLowerCase();
  if (s === "open") return { cls: "bg-[#DCFCE7] text-[#16A34A]", label: t("status.open") };
  if (s === "closed") return { cls: "bg-[#FEE2E2] text-[#DC2626]", label: t("status.closed") };
  if (s === "operational") return { cls: "bg-[#DBEAFE] text-[#2563EB]", label: t("status.operational") };
  return { cls: "bg-[#F3F4F6] text-[#6B7280]", label: status || "—" };
}

function StarRow({ rating }) {
  const full = Math.floor(rating);
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={
            "w-3.5 h-3.5 " +
            (i < full ? "fill-[#FBBF24] text-[#FBBF24]" : "text-[#E5E7EB]")
          }
        />
      ))}
      <span className="text-[12px] text-[#6B7280] ml-1">{rating?.toFixed(1)}</span>
    </div>
  );
}

export default function LeadCard({ lead, onSave, onAudit, onEmail, onMaps, onScoreBreakdown, onSimilar, onSequence, index = 0 }) {
  const { t, lang } = useLang();
  const meta = badgeMeta(lead.badge);
  const sm = statusMeta(lead.status, t);
  const types = (lead.types || []).filter((t) => t !== "establishment").slice(0, 3);
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      className="al-card al-fade-up p-5 flex flex-col gap-3"
      style={{ animationDelay: `${Math.min(index * 50, 400)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <h3
          data-testid={`lead-name-${lead.id}`}
          className="font-bold text-[16px] text-[#111827] leading-snug"
        >
          {lead.name}
        </h3>
        <span data-testid={`lead-badge-${lead.id}`} className={meta.cls}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
          {meta.label}
        </span>
      </div>

      <div className="space-y-1.5 text-[13px] text-[#4B5563]">
        <div className="flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#9CA3AF] shrink-0" />
          <span className="leading-snug">{lead.address}</span>
        </div>
        {lead.phone && (
          <div className="flex items-center">
            <PhoneAction phone={lead.phone_international || lead.phone}
              isPt={lang === "pt"} testid={`lead-phone-${lead.id}`} />
          </div>
        )}
        {lead.email && (
          <div className="flex items-center">
            <EmailAction email={lead.email} isPt={lang === "pt"} testid={`lead-email-${lead.id}`}
              context={{ name: lead.decision_maker?.name, company: lead.name,
                sector: lead.sector, address: lead.address, website: lead.website }} />
          </div>
        )}
        {lead.website && (
          <a
            data-testid={`lead-website-${lead.id}`}
            href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-[#3B82F6] transition-colors group"
            title={lead.website}
          >
            <Globe className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#3B82F6]" />
            <span className="truncate underline-offset-2 group-hover:underline">{lead.website}</span>
            <ExternalLink className="w-3 h-3 text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
        {lead.decision_maker?.name && (
          <div
            data-testid={`lead-dm-${lead.id}`}
            className="flex items-start gap-2 text-[12.5px]"
          >
            <UserCircle2 className="w-3.5 h-3.5 mt-0.5 text-[#C9A24E] shrink-0" />
            <span className="leading-snug">
              <span className="font-semibold text-[#1F2937]">{lead.decision_maker.name}</span>
              {lead.decision_maker.role && (
                <span className="text-[#6B7280]"> · {lead.decision_maker.role}</span>
              )}
            </span>
          </div>
        )}
        {(lead.linkedin_url || lead.instagram_url) && (
          <div className="flex items-center gap-2 pt-0.5">
            {lead.linkedin_url && (
              <a
                href={lead.linkedin_url}
                target="_blank" rel="noopener noreferrer"
                data-testid={`lead-linkedin-${lead.id}`}
                title="LinkedIn"
                className="w-7 h-7 rounded-md bg-[#0A66C2]/10 hover:bg-[#0A66C2] hover:text-white text-[#0A66C2] flex items-center justify-center transition-colors"
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
            )}
            {lead.instagram_url && (
              <a
                href={lead.instagram_url}
                target="_blank" rel="noopener noreferrer"
                data-testid={`lead-instagram-${lead.id}`}
                title="Instagram"
                className="w-7 h-7 rounded-md bg-gradient-to-br from-[#E1306C]/15 to-[#F77737]/15 hover:from-[#E1306C] hover:to-[#F77737] hover:text-white text-[#E1306C] flex items-center justify-center transition-colors"
              >
                <Instagram className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Status + Types */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          data-testid={`lead-status-${lead.id}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${sm.cls}`}
        >
          <Clock className="w-3 h-3" /> {sm.label}
        </span>
        {types.length > 0 && types.map((t) => (
          <span
            key={t}
            data-testid={`lead-type-${lead.id}-${t}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#EFF6FF] text-[#3B82F6]"
            title={t}
          >
            <Tag className="w-3 h-3" /> {t.replace(/_/g, " ")}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <StarRow rating={lead.rating || 0} />
        <button
          type="button"
          data-testid={`score-breakdown-btn-${lead.id}`}
          onClick={() => onScoreBreakdown && onScoreBreakdown(lead)}
          title={t("lead.score_tooltip")}
          className="text-right group cursor-pointer hover:bg-[#FAFAFB] rounded-md px-2 -mx-2 py-1 -my-1 transition-colors"
        >
          <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] group-hover:text-[#3B82F6] flex items-center gap-1 justify-end">
            {t("lead.score_label")} <Activity className="w-3 h-3" />
          </div>
          <div className="text-[18px] font-bold text-[#111827] group-hover:text-[#3B82F6]">{lead.digital_score}/100</div>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2">
        <button
          data-testid={`save-crm-btn-${lead.id}`}
          onClick={() => onSave(lead)}
          className="al-btn-primary flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium"
        >
          <Save className="w-3.5 h-3.5" /> {t("lead.save_crm")}
        </button>
        <button
          data-testid={`run-audit-btn-${lead.id}`}
          onClick={() => onAudit(lead)}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium border border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
        >
          <FileSearch className="w-3.5 h-3.5" /> {t("lead.run_audit")}
        </button>
        <button
          data-testid={`send-email-btn-${lead.id}`}
          onClick={() => onEmail(lead)}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium border border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
        >
          <Mail className="w-3.5 h-3.5" /> {t("lead.send_email")}
        </button>
        <button
          data-testid={`view-maps-btn-${lead.id}`}
          onClick={() => onMaps(lead)}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium border border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" /> {t("lead.view_maps")}
        </button>
      </div>
      {onSimilar && (
        <button
          data-testid={`similar-leads-btn-${lead.id}`}
          onClick={() => onSimilar(lead)}
          title={t("lead.similar_tooltip") || "Ver leads similares"}
          className="mt-1 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold border border-dashed border-[#A855F7] text-[#A855F7] hover:bg-[#FAF5FF] transition-colors"
        >
          <Layers className="w-3.5 h-3.5" /> {t("lead.similar") || "Similares"}
        </button>
      )}
      {onSequence && (
        <button
          data-testid={`sequence-btn-${lead.id}`}
          onClick={() => onSequence(lead)}
          title={t("lead.sequence_tooltip") || "Iniciar sequência de cold-email"}
          className="mt-1 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity"
        >
          <Send className="w-3.5 h-3.5" /> {t("lead.sequence") || "Iniciar Sequência"}
        </button>
      )}
    </div>
  );
}
